import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LocLink from '../components/LocLink';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setStatus({ type: 'success', message: t('contact.form.success') });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: t('contact.form.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-20 sm:pt-24">
      {/* Hero */}
      <section className="bg-neutral-50 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1a1a1a] mb-6">
              {t('contact.title')}
            </h1>
            <p className="font-manrope text-lg sm:text-xl text-neutral-600">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-outfit font-bold text-2xl text-[#1a1a1a] mb-8">
                {language === 'fr' ? 'Informations de contact' : 'Contact Information'}
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#E31B23]" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-bold text-[#1a1a1a] mb-1">
                      {language === 'fr' ? 'Adresse' : 'Location'}
                    </h3>
                    <p className="font-manrope text-neutral-600">
                      {t('contact.info.location')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#E31B23]" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-bold text-[#1a1a1a] mb-1">
                      {language === 'fr' ? 'Courriel' : 'Email'}
                    </h3>
                    <p className="font-manrope text-neutral-600">
                      {t('contact.info.email')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="bg-neutral-100 rounded-3xl aspect-video flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
                  <p className="font-manrope text-neutral-500">Baie-Comeau, Québec</p>
                  <p className="font-manrope text-xs text-neutral-400 mt-1">
                    {language === 'fr' ? 'Con\u00e7u et fabriqu\u00e9 au Canada' : 'Designed and manufactured in Canada'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div
                className="mb-6 bg-neutral-50 border border-neutral-200 rounded-2xl p-4"
                data-testid="contact-privacy-notice"
              >
                <p className="font-manrope text-sm text-neutral-700 leading-relaxed">
                  {language === 'fr'
                    ? 'Aucun renseignement m\u00e9dical personnel n\u2019est n\u00e9cessaire. Veuillez ne pas transmettre de diagnostic, de num\u00e9ro de prescription ou d\u2019autre renseignement m\u00e9dical confidentiel. Pour v\u00e9rifier la compatibilit\u00e9 d\u2019un \u00e9tui, une photo des fournitures et leurs dimensions approximatives suffisent. Consultez notre '
                    : 'No personal medical information is required. Please do not submit diagnoses, prescription numbers, or other confidential medical information. To verify case compatibility, a photo of the supplies and their approximate dimensions is sufficient. Review our '}
                  <LocLink to="privacy" className="text-[#E31B23] font-semibold underline decoration-dotted underline-offset-2">
                    {language === 'fr' ? 'Politique de confidentialit\u00e9' : 'Privacy Policy'}
                  </LocLink>
                  .
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block font-manrope font-medium text-neutral-700 mb-2"
                  >
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#E31B23] focus:ring-2 focus:ring-[#E31B23]/20 outline-none transition-all font-manrope"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block font-manrope font-medium text-neutral-700 mb-2"
                  >
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#E31B23] focus:ring-2 focus:ring-[#E31B23]/20 outline-none transition-all font-manrope"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block font-manrope font-medium text-neutral-700 mb-2"
                  >
                    {t('contact.form.subject')}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#E31B23] focus:ring-2 focus:ring-[#E31B23]/20 outline-none transition-all font-manrope"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block font-manrope font-medium text-neutral-700 mb-2"
                  >
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#E31B23] focus:ring-2 focus:ring-[#E31B23]/20 outline-none transition-all font-manrope resize-none"
                    data-testid="contact-message"
                  />
                </div>

                {status.type && (
                  <div
                    className={`flex items-center gap-2 p-4 rounded-xl ${
                      status.type === 'success'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    <span className="font-manrope">{status.message}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-[#E31B23] hover:bg-[#c8171e] disabled:bg-red-300 text-white px-6 py-4 rounded-xl font-bold transition-all duration-300 active:scale-95"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <span>{t('contact.form.sending')}</span>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>{t('contact.form.send')}</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
